import React, { useEffect, useRef } from "react";
import "../styles/Pagination.css";

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  scrollToTopOnChange = true,
  showSummary = true,
  compact = false,
  className = "",
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage)); // ensure at least 1 page
  const previousTotalItemsRef = useRef(totalItems);

  const scrollToPageTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handlePageChange = (nextPage) => {
    if (nextPage === currentPage) return;

    onPageChange(nextPage);
    if (scrollToTopOnChange) {
      scrollToPageTop();
    }
  };

  useEffect(() => {
    const previousTotalItems = previousTotalItemsRef.current;

    if (currentPage > totalPages) {
      onPageChange(totalPages);
    } else if (currentPage > 1 && totalItems !== previousTotalItems) {
      onPageChange(1);
    }

    previousTotalItemsRef.current = totalItems;
  }, [currentPage, onPageChange, totalItems, totalPages]);

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, "ellipsis-end", totalPages];
    }

    if (currentPage >= totalPages - 3) {
      return [
        1,
        "ellipsis-start",
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      "ellipsis-start",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis-end",
      totalPages,
    ];
  };

  const firstVisibleItem =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const lastVisibleItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (!showSummary && totalPages <= 1) return null;

  return (
    <div
      className={`app-pagination ${
        compact ? "app-pagination--compact" : ""
      } ${!showSummary ? "app-pagination--without-summary" : ""} ${className}`.trim()}
    >
      {showSummary && (
        <div className="app-pagination-summary" aria-live="polite">
          <span>Showing</span>
          <strong>
            {firstVisibleItem}–{lastVisibleItem}
          </strong>
          <span>of</span>
          <strong>{totalItems}</strong>
          <span>entries</span>
        </div>
      )}
      {totalPages > 1 && (
        <nav className="app-pagination-nav" aria-label="Table pagination">
        <ul className="pagination mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link app-pagination-direction"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
            >
              <span aria-hidden="true">‹</span>
              <span className="app-pagination-direction-label">Previous</span>
            </button>
          </li>
          {getPaginationItems().map((item) =>
            typeof item === "number" ? (
              <li
                key={item}
                className={`page-item ${
                  item === currentPage ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="page-link"
                  onClick={() => handlePageChange(item)}
                  aria-label={`Go to page ${item}`}
                  aria-current={item === currentPage ? "page" : undefined}
                >
                  {item}
                </button>
              </li>
            ) : (
              <li key={item} className="page-item disabled" aria-hidden="true">
                <span className="page-link app-pagination-ellipsis">…</span>
              </li>
            )
          )}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              type="button"
              className="page-link app-pagination-direction"
              onClick={() =>
                handlePageChange(Math.min(currentPage + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
            >
              <span className="app-pagination-direction-label">Next</span>
              <span aria-hidden="true">›</span>
            </button>
          </li>
        </ul>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
